import { getField, Locales } from "@simplyk/common";

type QuestionForArchiveCheck = {
  questionFields?: { locale: Locales; body: string }[] | null;
  rateQuestions?: { rateId: string }[] | null;
};

type RateForArchiveCheck = {
  id: string;
};

// Names the per-attendee questions the API will archive along with these ticket types, so the card can
// ask the organizer to confirm losing them before the type is removed from the form.
export const getQuestionsArchivedByRateDeletion = ({
  questions,
  liveRates,
  rateIdsToDelete,
  formRateIds,
  locale,
}: {
  questions: QuestionForArchiveCheck[];
  /** Sellable ticket types only: add-ons and variant children never carry a per-attendee question. */
  liveRates: RateForArchiveCheck[];
  rateIdsToDelete: string[];
  /**
   * Sellable ticket types in the form, including ones not saved yet. A question assigned to every
   * saved type is inherited by a new type before the old one is archived
   * (`TicketingService.syncRateQuestionsForNewRates`), so swapping the only type keeps the question
   * and must not be reported as a loss.
   */
  formRateIds?: string[];
  locale: Locales;
}): string[] => {
  if (rateIdsToDelete.length === 0) {
    return [];
  }

  const deletedIds = new Set(rateIdsToDelete);
  const liveIds = liveRates.map((rate) => rate.id);
  const survivingIds = new Set(liveIds.filter((id) => !deletedIds.has(id)));
  const hasNewRate = (formRateIds ?? []).some((id) => !liveIds.includes(id));

  return questions
    .filter((question) => {
      const assignedRateIds = question.rateQuestions?.map((rateQuestion) => rateQuestion.rateId) ?? [];
      const losesARate = assignedRateIds.some((rateId) => deletedIds.has(rateId));
      const keepsARate = assignedRateIds.some((rateId) => survivingIds.has(rateId));
      const inheritsNewRate = hasNewRate && liveIds.every((id) => assignedRateIds.includes(id));
      return losesARate && !keepsARate && !inheritsNewRate;
    })
    .map((question) => getField(question.questionFields, locale)?.body ?? "");
};
